import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { useAuth } from "../contexts/AuthContext";
import {
  AcademicCapIcon,
  UserCircleIcon,
  UserIcon,
  EnvelopeIcon,
  LockClosedIcon,
  CalendarIcon,
  PhoneIcon,
  MapPinIcon,
  BuildingOfficeIcon,
  GlobeAsiaAustraliaIcon,
  XCircleIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ChevronRightIcon,
  ChevronLeftIcon,
} from "@heroicons/react/24/outline";

interface RegisterFormData {
  name: string;
  email: string;
  registration_code?: string;
  password: string;
  confirmPassword: string;
  age?: number;
  village?: string;
  district?: string;
  province?: string;
  date_of_birth?: string;
  phone?: string;
}

const Register: React.FC = () => {
  const {
    register: registerForm,
    handleSubmit,
    watch,
    getValues,
    formState: { errors },
  } = useForm<RegisterFormData>();
  const { register } = useAuth();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [profileImage, setProfileImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [currentStep, setCurrentStep] = useState(1);

  const password = watch("password");

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setProfileImage(file);

      // Create preview URL
      const reader = new FileReader();
      reader.onload = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const onSubmit = async (data: RegisterFormData) => {
    setIsLoading(true);
    setError("");
    setSuccess(false);

    try {
      // Create FormData for multipart/form-data submission
      const formData = new FormData();
      formData.append("name", data.name);
      formData.append("email", data.email);
      if (data.registration_code)
        formData.append("registration_code", data.registration_code);
      formData.append("password", data.password);

      if (data.age) formData.append("age", data.age.toString());
      if (data.village) formData.append("village", data.village);
      if (data.district) formData.append("district", data.district);
      if (data.province) formData.append("province", data.province);
      if (data.date_of_birth)
        formData.append("date_of_birth", data.date_of_birth);
      if (data.phone) formData.append("phone", data.phone);
      if (profileImage) formData.append("profile_image", profileImage);

      const response = await register(formData);
      setSuccess(true);

      // Redirect to login after 10 seconds
      setTimeout(() => {
        navigate("/login");
      }, 10000);
    } catch (err: any) {
      setError(
        err.response?.data?.message || "ການລົງທະບຽນລົ້ມເຫລວ. ກະລຸນາລອງໃໝ່.",
      );
    } finally {
      setIsLoading(false);
    }
  };

  const nextStep = () => {
    setCurrentStep((prev) => Math.min(prev + 1, 2));
  };

  const prevStep = () => {
    setCurrentStep((prev) => Math.max(prev - 1, 1));
  };

  const handleStepSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (currentStep < 2) {
      nextStep();
    } else {
      handleSubmit(onSubmit)(e);
    }
  };

  const renderStepIndicator = () => (
    <div className="flex items-center justify-center mb-6 sm:mb-8">
      {[1, 2].map((step) => (
        <div key={step} className="flex items-center">
          <div
            className={`w-6 h-6 sm:w-8 sm:h-8 rounded-full flex items-center justify-center text-xs sm:text-sm font-semibold ${
              step === currentStep
                ? "bg-indigo-600 text-white"
                : step < currentStep
                  ? "bg-green-500 text-white"
                  : "bg-gray-200 text-gray-600"
            }`}
          >
            {step < currentStep ? "✓" : step}
          </div>
          {step < 2 && (
            <div
              className={`w-8 sm:w-12 h-1 mx-1 sm:mx-2 ${
                step < currentStep ? "bg-green-500" : "bg-gray-200"
              }`}
            />
          )}
        </div>
      ))}
    </div>
  );

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-4 sm:space-y-6">
            <div className="text-center mb-4 sm:mb-6">
              <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2">
                ຂໍ້ມູນພື້ນຖານ
              </h3>
              <p className="text-xs sm:text-sm text-gray-600">
                ປ້ອນຂໍ້ມູນສ່ວນຕົວຂອງທ່ານ
              </p>
            </div>

            {/* Profile Image */}
            <div className="text-center">
              <div className="relative inline-block">
                <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full overflow-hidden bg-gradient-to-r from-indigo-300 to-purple-300 flex items-center justify-center shadow-lg border-2 border-white">
                  {imagePreview ? (
                    <img
                      src={imagePreview}
                      alt="Preview"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <UserCircleIcon className="w-8 h-8 sm:w-10 sm:h-10 lg:w-12 lg:h-12 text-white" />
                  )}
                </div>
                {imagePreview && (
                  <button
                    type="button"
                    onClick={() => {
                      setProfileImage(null);
                      setImagePreview(null);
                    }}
                    className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-colors"
                    title="ລຶບຮູບ"
                    aria-label="ລຶບຮູບ"
                  >
                    <XCircleIcon className="h-3 w-3 sm:h-4 sm:w-4" />
                  </button>
                )}
              </div>
              <input
                type="file"
                accept="image/*"
                onChange={handleImageChange}
                className="hidden"
                id="profile-image"
              />
              <label
                htmlFor="profile-image"
                className="cursor-pointer inline-block mt-2 sm:mt-3 bg-white py-2 px-3 sm:px-4 border border-gray-300 rounded-lg shadow-sm text-xs sm:text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all transform hover:scale-105"
              >
                ເລືອກຮູບໂປຣໄຟລ໌ຂອງທ່ານ
              </label>
            </div>

            {/* Name and Email */}
            <div className="space-y-3 sm:space-y-4">
              <div>
                <label
                  htmlFor="name"
                  className="block text-xs sm:text-sm font-medium text-gray-700 mb-1"
                >
                  ຊື່ ແລະ ນາມສະກຸນ <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <UserIcon className="h-4 w-4 sm:h-5 sm:w-5 text-gray-400" />
                  </div>
                  <input
                    {...registerForm("name", { required: "ຕ້ອງການຊື່" })}
                    type="text"
                    autoComplete="name"
                    className="w-full pl-10 pr-3 py-2.5 sm:py-3 border border-gray-300 rounded-lg placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 transition-colors text-[10px] sm:text-xs"
                    placeholder="ໃສຊື່ ແລະ ນາມສະກຸນຂອງທ່ານບ່ອນນີ້"
                  />
                </div>
                {errors.name && (
                  <p className="mt-1 text-xs sm:text-sm text-red-600 flex items-center">
                    <ExclamationTriangleIcon className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
                    {errors.name.message}
                  </p>
                )}
              </div>

              <div>
                <label
                  htmlFor="email"
                  className="block text-xs sm:text-sm font-medium text-gray-700 mb-1"
                >
                  ຕັ້ງອີເມວສຳລັບເຂົ້າສູ່ລະບົບການຮຽນ (ນັກຮຽນສາມາດຕັ້ງເອງໄດ້ເລີຍ ບໍ່ຈຳເປັນຕ້ອງໃສ່ອີເມວແທ້){" "}
                  <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <EnvelopeIcon className="h-4 w-4 sm:h-5 sm:w-5 text-gray-400" />
                  </div>
                  <input
                    {...registerForm("email", {
                      required: "ຕ້ອງການອີເມວ",
                      pattern: {
                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                        message: "ທີ່ຢູ່ອີເມວບໍ່ຖືກຕ້ອງ",
                      },
                    })}
                    type="email"
                    autoComplete="email"
                    className="w-full pl-10 pr-3 py-2.5 sm:py-3 border border-gray-300 rounded-lg placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 transition-colors text-[10px] sm:text-xs"
                    placeholder="ຢ່າລືມໃສ່ @gmail.com"
                  />
                </div>
                {errors.email && (
                  <p className="mt-1 text-xs sm:text-sm text-red-600 flex items-center">
                    <ExclamationTriangleIcon className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
                    {errors.email.message}
                  </p>
                )}
              </div>

              <div>
                <label
                  htmlFor="password"
                  className="block text-xs sm:text-sm font-medium text-gray-700 mb-1"
                >
                  ຕັ້ງລະຫັດຜ່ານຂອງທ່ານ <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <LockClosedIcon className="h-4 w-4 sm:h-5 sm:w-5 text-gray-400" />
                  </div>
                  <input
                    {...registerForm("password", {
                      required: "ຕ້ອງການລະຫັດຜ່ານ",
                      minLength: {
                        value: 4,
                        message: "ລະຫັດຜ່ານຕ້ອງມີຢ່າງໜ້ອຍ 4 ຕົວອັກສອນ",
                      },
                    })}
                    type="password"
                    autoComplete="new-password"
                    className="w-full pl-10 pr-3 py-2.5 sm:py-3 border border-gray-300 rounded-lg placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 transition-colors text-[10px] sm:text-xs"
                    placeholder="ລະຫັດນີ້ຈະໃຊ້ສຳລັບການເຂົ້າສູ່ລະບົບການຮຽນ"
                  />
                </div>
                {errors.password && (
                  <p className="mt-1 text-xs sm:text-sm text-red-600 flex items-center">
                    <ExclamationTriangleIcon className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
                    {errors.password.message}
                  </p>
                )}
              </div>

              <div>
                <label
                  htmlFor="confirmPassword"
                  className="block text-xs sm:text-sm font-medium text-gray-700 mb-1"
                >
                  ⁠ຢືນຢັນລະຫັດຜ່ານ <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <LockClosedIcon className="h-4 w-4 sm:h-5 sm:w-5 text-gray-400" />
                  </div>
                  <input
                    {...registerForm("confirmPassword", {
                      required: "ກະລຸນາຢືນຢັນລະຫັດຜ່ານຂອງທ່ານ",
                      validate: (value) =>
                        value === password || "ລະຫັດຜ່ານບໍ່ກົງກັນ",
                    })}
                    type="password"
                    autoComplete="new-password"
                    className="w-full pl-10 pr-3 py-2.5 sm:py-3 border border-gray-300 rounded-lg placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 transition-colors text-[10px] sm:text-xs"
                    placeholder="ຢືນຢັນລະຫັດຜ່ານອີກຄັ້ງ"
                  />
                </div>
                {errors.confirmPassword && (
                  <p className="mt-1 text-xs sm:text-sm text-red-600 flex items-center">
                    <ExclamationTriangleIcon className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
                    {errors.confirmPassword.message}
                  </p>
                )}
              </div>

              <div>
                <label
                  htmlFor="registration_code"
                  className="block text-xs sm:text-sm font-medium text-gray-700 mb-1"
                >
                  ລະຫັດລົງທະບຽນຮຽນ
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <AcademicCapIcon className="h-4 w-4 sm:h-5 sm:w-5 text-gray-400" />
                  </div>
                  <input
                    {...registerForm("registration_code")}
                    type="text"
                    className="w-full pl-10 pr-3 py-2.5 sm:py-3 border border-gray-300 rounded-lg placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 transition-colors text-[10px] sm:text-xs"
                    placeholder="ລະຫັດລົງທະບຽນຮຽນ (ຖ້າມີ)"
                  />
                </div>
              </div>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-4 sm:space-y-6">
            <div className="text-center mb-4 sm:mb-6">
              <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2">
                ຂໍ້ມູນເພີ່ມເຕີມ
              </h3>
              <p className="text-xs sm:text-sm text-gray-600">
                ຂໍ້ມູນສ່ວນຕົວເພີ່ມເຕີມ (ທາງເລືອກ)
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              <div>
                <label
                  htmlFor="age"
                  className="block text-xs sm:text-sm font-medium text-gray-700 mb-1"
                >
                  ອາຍຸ
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <span className="text-gray-400 text-xs sm:text-sm">
                      ອາຍຸ
                    </span>
                  </div>
                  <input
                    {...registerForm("age", {
                      valueAsNumber: true,
                      min: { value: 1, message: "ອາຍຸຕ້ອງມາກວ່າ 1" },
                      max: { value: 120, message: "ອາຍຸຕ້ອງນ້ອຍກວ່າ 120" },
                    })}
                    type="number"
                    className="w-full pl-12 pr-3 py-2.5 sm:py-3 border border-gray-300 rounded-lg placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 transition-colors text-sm"
                    placeholder="25"
                  />
                </div>
                {errors.age && (
                  <p className="mt-1 text-xs sm:text-sm text-red-600 flex items-center">
                    <ExclamationTriangleIcon className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
                    {errors.age.message}
                  </p>
                )}
              </div>

              <div>
                <label
                  htmlFor="phone"
                  className="block text-xs sm:text-sm font-medium text-gray-700 mb-1"
                >
                  ເບີໂທລະສັບ
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <PhoneIcon className="h-4 w-4 sm:h-5 sm:w-5 text-gray-400" />
                  </div>
                  <input
                    {...registerForm("phone", {
                      pattern: {
                        value: /^[+]?[\d\s\-\(\)]{10,20}$/,
                        message: "ຮູບແບບເບີໂທລະສັບບໍ່ຖືກຕ້ອງ",
                      },
                    })}
                    type="tel"
                    autoComplete="tel"
                    className="w-full pl-10 pr-3 py-2.5 sm:py-3 border border-gray-300 rounded-lg placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 transition-colors text-sm"
                    placeholder="+85620xxxxxxx"
                  />
                </div>
                {errors.phone && (
                  <p className="mt-1 text-xs sm:text-sm text-red-600 flex items-center">
                    <ExclamationTriangleIcon className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
                    {errors.phone.message}
                  </p>
                )}
              </div>

              <div>
                <label
                  htmlFor="date_of_birth"
                  className="block text-xs sm:text-sm font-medium text-gray-700 mb-1"
                >
                  ວັນເກີດ
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <CalendarIcon className="h-4 w-4 sm:h-5 sm:w-5 text-gray-400" />
                  </div>
                  <input
                    {...registerForm("date_of_birth")}
                    type="date"
                    className="w-full pl-10 pr-3 py-2.5 sm:py-3 border border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 transition-colors text-sm"
                  />
                </div>
                {errors.date_of_birth && (
                  <p className="mt-1 text-xs sm:text-sm text-red-600 flex items-center">
                    <ExclamationTriangleIcon className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
                    {errors.date_of_birth.message}
                  </p>
                )}
              </div>

              <div>
                <label
                  htmlFor="village"
                  className="block text-xs sm:text-sm font-medium text-gray-700 mb-1"
                >
                  ບ້ານ
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <MapPinIcon className="h-4 w-4 sm:h-5 sm:w-5 text-gray-400" />
                  </div>
                  <input
                    {...registerForm("village")}
                    type="text"
                    className="w-full pl-10 pr-3 py-2.5 sm:py-3 border border-gray-300 rounded-lg placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 transition-colors text-sm"
                    placeholder="ຊື່ບ້ານ"
                  />
                </div>
                {errors.village && (
                  <p className="mt-1 text-xs sm:text-sm text-red-600 flex items-center">
                    <ExclamationTriangleIcon className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
                    {errors.village.message}
                  </p>
                )}
              </div>

              <div>
                <label
                  htmlFor="district"
                  className="block text-xs sm:text-sm font-medium text-gray-700 mb-1"
                >
                  ເມືອງ
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <BuildingOfficeIcon className="h-4 w-4 sm:h-5 sm:w-5 text-gray-400" />
                  </div>
                  <input
                    {...registerForm("district")}
                    type="text"
                    className="w-full pl-10 pr-3 py-2.5 sm:py-3 border border-gray-300 rounded-lg placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 transition-colors text-sm"
                    placeholder="ຊື່ເມືອງ"
                  />
                </div>
                {errors.district && (
                  <p className="mt-1 text-xs sm:text-sm text-red-600 flex items-center">
                    <ExclamationTriangleIcon className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
                    {errors.district.message}
                  </p>
                )}
              </div>

              <div>
                <label
                  htmlFor="province"
                  className="block text-xs sm:text-sm font-medium text-gray-700 mb-1"
                >
                  ແຂວງ
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <GlobeAsiaAustraliaIcon className="h-4 w-4 sm:h-5 sm:w-5 text-gray-400" />
                  </div>
                  <input
                    {...registerForm("province")}
                    type="text"
                    className="w-full pl-10 pr-3 py-2.5 sm:py-3 border border-gray-300 rounded-lg placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 transition-colors text-sm"
                    placeholder="ຊື່ແຂວງ"
                  />
                </div>
                {errors.province && (
                  <p className="mt-1 text-xs sm:text-sm text-red-600 flex items-center">
                    <ExclamationTriangleIcon className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
                    {errors.province.message}
                  </p>
                )}
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-indigo-50 via-purple-50 to-blue-50 py-6 sm:py-8 lg:py-12 px-3 sm:px-4 lg:px-6 xl:px-8">
      <div className="max-w-sm sm:max-w-md w-full space-y-6 sm:space-y-8">
        <div className="bg-white rounded-xl sm:rounded-2xl shadow-xl p-6 sm:p-8 border border-indigo-100">
          <div className="flex justify-start">
            <div className="bg-gradient-to-r from-indigo-500 to-purple-600 rounded-full p-2 sm:p-3 shadow-lg">
              <UserIcon className="h-8 w-8 sm:h-10 sm:w-10 lg:h-12 lg:w-12 text-white" />
            </div>
            <h2 className="mt-4 sm:mt-6 ml-3 sm:ml-4 text-start text-2xl sm:text-3xl font-extrabold text-gray-900">
              ສ້າງບັນຊີໃໝ່
            </h2>
          </div>

          <div className="mt-4 sm:mt-6 flex flex-col sm:flex-row justify-between items-start sm:items-center space-y-3 sm:space-y-0">
            <Link
              to="/"
              className="flex items-center text-gray-600 hover:text-indigo-600 transition-colors font-medium text-sm"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-4 w-4 sm:h-5 sm:w-5 mr-1"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M10 19l-7-7m0 0l7-7m-7 7h18"
                />
              </svg>
              ກັບຄືນ
            </Link>
          </div>

          <form
            onSubmit={handleStepSubmit}
            className="mt-6 sm:mt-8 space-y-4 sm:space-y-6"
          >
            {/* Status Messages */}
            {error && (
              <div className="rounded-lg bg-red-50 p-3 sm:p-4 border border-red-200">
                <div className="flex items-center">
                  <ExclamationTriangleIcon className="h-5 w-5 sm:h-6 sm:w-6 text-red-500 mr-2 sm:mr-3" />
                  <div className="text-xs sm:text-sm text-red-800">{error}</div>
                </div>
              </div>
            )}

            {success && (
              <div className="rounded-lg bg-green-50 p-4 sm:p-6 border border-green-200 text-center animate-fade-in-up">
                <CheckCircleIcon className="mx-auto h-12 w-12 sm:h-16 sm:w-16 text-green-500 mb-4" />
                <h3 className="text-xl sm:text-2xl font-bold text-green-800 mb-2">
                  ຂໍສະແດງຄວາມຍິນດີນຳ
                </h3>
                <p className="text-green-700 font-medium mb-5 text-sm sm:text-base">
                  ທ່ານໄດ້ລົງທະບຽນຮຽນສຳເລັດແລ້ວ
                </p>

                <div className="bg-white rounded-lg border border-green-200 p-4 sm:p-5 text-left max-w-sm mx-auto mb-5 shadow-sm">
                  <p className="text-sm sm:text-base text-gray-700 mb-3">
                    <span className="font-semibold text-gray-900 block mb-1">
                      ອີເມວສຳລັບ Login ຂອງທ່ານແມ່ນ:
                    </span>
                    <span className="text-indigo-700 font-mono bg-indigo-50 px-3 py-1.5 rounded-md inline-block w-full break-all border border-indigo-100">
                      {getValues("email")}
                    </span>
                  </p>
                  <p className="text-sm sm:text-base text-gray-700 text-left">
                    <span className="font-semibold text-gray-900 block mb-1">
                      ລະຫັດຂອງທ່ານແມ່ນ:
                    </span>
                    <span className="text-indigo-700 font-mono bg-indigo-50 px-3 py-1.5 rounded-md inline-block w-full break-all border border-indigo-100">
                      {getValues("password")}
                    </span>
                  </p>
                </div>

                <div className="flex flex-col sm:flex-row items-center justify-center space-y-3 sm:space-y-0 sm:space-x-4">
                  <p className="text-xs sm:text-sm text-green-600 font-medium">
                    ກຳລັງພາທ່ານໄປຫນ້າເຂົ້າລະບົບໃນ 10 ວິນາທີ...
                  </p>
                  <Link
                    to="/login"
                    className="inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-colors shadow-sm"
                  >
                    ເຂົ້າສູ່ລະບົບດຽວນີ້
                  </Link>
                </div>
              </div>
            )}

            {/* Step Indicator */}
            {renderStepIndicator()}

            {/* Step Content */}
            {renderStepContent()}

            <div className="bg-amber-50 rounded-lg p-3 sm:p-4 border border-amber-200 mt-4">
              <p className="text-xs sm:text-sm text-amber-800 flex items-start sm:items-center">
                <ExclamationTriangleIcon className="h-5 w-5 mr-2 flex-shrink-0 text-amber-500" />
                <span>
                  ກະລຸນາຈື່ອີເມວ ແລະ ລະຫັດທີ່ທ່ານຕັ້ງໃຫ້ດີ
                  ເພາະຈະໃຊ້ສຳລັບການເຂົ້າສູ່ລະບົບການຮຽນທຸກໆຄັ້ງ
                </span>
              </p>
            </div>

            {/* Navigation Buttons */}
            <div className="flex justify-between pt-4 sm:pt-6">
              {currentStep > 1 && (
                <button
                  type="button"
                  onClick={prevStep}
                  className="flex items-center px-3 sm:px-4 py-2 border border-gray-300 rounded-lg text-xs sm:text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all"
                >
                  <ChevronLeftIcon className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
                  ກັບຄືນ
                </button>
              )}

              <div className="flex-1"></div>

              <button
                type="submit"
                disabled={isLoading}
                className="flex items-center px-4 sm:px-6 py-2 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-lg shadow-md hover:from-indigo-700 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all transform hover:scale-105 font-medium text-xs sm:text-sm"
              >
                {isLoading ? (
                  <>
                    <svg
                      className="animate-spin -ml-1 mr-2 h-3 w-3 sm:h-4 sm:w-4 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    ກຳລັງສ້າງບັນຊີ...
                  </>
                ) : currentStep < 2 ? (
                  <>
                    ຕໍ່ໄປ
                    <ChevronRightIcon className="h-3 w-3 sm:h-4 sm:w-4 ml-1" />
                  </>
                ) : (
                  <>
                    <UserIcon className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
                    ສ້າງບັນຊີ
                  </>
                )}
              </button>
            </div>

            {/* Terms and Conditions */}
            <div className="bg-indigo-50 rounded-lg p-3 sm:p-4 border border-indigo-100">
              <p className="text-center text-xs sm:text-sm text-indigo-700">
                ໂດຍການລົງທະບຽນ ທ່ານຍອມຮັບເງື່ອນໄຂການໃຊ້ງານ ແລະ
                ນະໂຍບາຍຄວາມປອດໄພຂອງພວກເຮົາ
              </p>
              <p className="mt-2 text-center text-xs text-indigo-500">
                <span className="text-red-500">*</span> ຂໍ້ມູນທີ່ຈຳເປັນ
              </p>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Register;
